// NB: An alternative option for better search speed but less customization is available
// http://elasticlunr.com/
import { memoize } from 'lodash/fp';
/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
import lunr, { Index } from 'lunr';
import da from 'lunr-languages/lunr.da.js';
import de from 'lunr-languages/lunr.de.js';
import es from 'lunr-languages/lunr.es.js';
import fi from 'lunr-languages/lunr.fi.js';
import fr from 'lunr-languages/lunr.fr.js';
import hu from 'lunr-languages/lunr.hu.js';
import it from 'lunr-languages/lunr.it.js';
import ja from 'lunr-languages/lunr.ja.js';
import multi from 'lunr-languages/lunr.multi.js';
import nl from 'lunr-languages/lunr.nl.js';
import no from 'lunr-languages/lunr.no.js';
import pt from 'lunr-languages/lunr.pt.js';
import ro from 'lunr-languages/lunr.ro.js';
import ru from 'lunr-languages/lunr.ru.js';
// Import lunr-languages as plugins
import stemmer from 'lunr-languages/lunr.stemmer.support.js';
import sv from 'lunr-languages/lunr.sv.js';
import tr from 'lunr-languages/lunr.tr.js';
import vi from 'lunr-languages/lunr.vi.js';
import tinyseg from 'lunr-languages/tinyseg.js';

// Refactor this to it's own file for reusability
stemmer(lunr);
tinyseg(lunr);
multi(lunr);
da(lunr);
de(lunr);
es(lunr);
fi(lunr);
fr(lunr);
hu(lunr);
it(lunr);
ja(lunr);
nl(lunr);
no(lunr);
pt(lunr);
ro(lunr);
ru(lunr);
sv(lunr);
tr(lunr);
vi(lunr);

interface MetaDataValue {
  body: {
    position: number[][];
  };
}
interface MetaData {
  [key: string]: MetaDataValue;
}

const ENGLISH_CHARS_REGEX = /^[A-Za-z0-9.!? ]*$/;

// TODO: [PER12719] Fix search accuracy regression due to introducing languages
// 14 languages supported from https://lunrjs.com/guides/language_support.html
// const LANGUAGES = [
//   'en',
//   'ar',
//   'da',
//   'de',
//   'du',
//   'es',
//   'fi',
//   'fr',
//   'hu',
//   'it',
//   'ja',
//   'nl',
//   'no',
//   'pt',
//   'ro',
//   'ru',
//   'sv',
//   'tr',
//   'vi',
// ];

// pages is an array of pages, where each page has a string[]
// each string represents a line on the page
const initializeSearchIndex = (pages: string[][]) => {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  return lunr(function (config) {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    this.ref('id');
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    this.field('body');
    // TODO: [PER12719] Fix search accuracy regression due to introducing languages
    // this.use(lunr.multiLanguage(...LANGUAGES));
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    this.metadataWhitelist = ['position'];
    // Lunr.js filters common english words (e.g 'your') to optimize search
    // Disable filtering of common english words used by lunrjs to find exact matches
    // https://github.com/olivernn/lunr.js/issues/233#issuecomment-246468462
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    config.pipeline.remove(lunr.stopWordFilter);
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    config.searchPipeline.remove(lunr.stopWordFilter);
    // Lunr.js abbreviates show in this map (https://lunrjs.com/docs/stemmer.js.html)
    // Disable stemmer to allow for exact word matches
    // https://github.com/olivernn/lunr.js/issues/304#issuecomment-336262546
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    config.pipeline.remove(lunr.stemmer);
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    config.searchPipeline.remove(lunr.stemmer);
    // Source code: https://lunrjs.com/docs/trimmer.js.html
    // Disable trimmer to allow for prepending or appending special characters
    // https://github.com/olivernn/lunr.js/issues/275
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    config.pipeline.remove(lunr.trimmer);
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    config.searchPipeline.remove(lunr.trimmer);

    pages.forEach((page, pageIdx) => {
      page.forEach((line, lineIdx) => {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        this.add({
          id: [pageIdx, lineIdx],
          body: line,
        });
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      }, this);
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    }, this);
  });
};

const memoizedInitializeSearchIndex = memoize(initializeSearchIndex);

export interface SearchMatch {
  startPosition: number;
  searchTerm: string;
  pageNumber: number;
  lineNumber: number;
}

export const getSearchMatches = (searchText: string, pages: string[][]): SearchMatch[] => {
  // Filter punctuations and trailing spaces to improve search accuracy for english words only
  // Non-english/latin characters to use original search words with trailing spaces removed
  const searchIndex = memoizedInitializeSearchIndex(pages);
  const searchTerms = ENGLISH_CHARS_REGEX.test(searchText)
    ? searchText
        .replace(/[^\w\s]|_/g, '')
        .split(' ')
        .filter((s) => s !== '')
    : searchText.split(' ').filter((s) => s !== '');
  // Add lunrjs wild cards https://lunrjs.com/guides/searching.html
  // Required for searching multiple strings as a cojunction of each
  // lunrjs by default searches each word uniquely (A OR B)
  // *{text}* required to allow suffix and prefix of search term to appear
  let matches: Index.Result[] = [];
  if (searchTerms.length > 1) {
    const searchString = `+*${searchTerms.join(' +')}*`;
    matches = searchIndex.search(searchString);
  } else {
    const prefixString = `*${searchTerms[0]}*`;
    const exactString = searchTerms[0];
    matches = searchIndex.search(`${prefixString} ${exactString}`);
  }

  return matches
    .flatMap((match) => {
      const pageIndex = parseInt(match.ref.split(',')[0]);
      const lineIndex = parseInt(match.ref.split(',')[1]);

      /* lunrjs looks for each word separately instead of treating the search term as a whole phrase.
     matches are returned for each term separately.
     for example let's say we have the text "the quick brown fox jumps over the lazy dog"
     and i am searching for the phrase "quick fox"
     lunrjs will return that there is a "match" and will return the following in the metadata
     {
        metadata: {
          quick: {
            body: {
              position: [[5, 5]] (first element is the position of the word in the line, second is the token length)
            }
          },
          fox: {
            body: {
              position: [[17, 3]]
            }
          }
        }
      }
      which is obviously not we want. so we need (1) to check that all the positions are conitnuous (with one space in between)
      to validate that the whole phrase is a match.
      lunrjs does not return the position of where the search term was found, but instead position of word that contains the search term.
      let's go back to our example of "the quick brown fox jumps over the lazy dog" and we're searching for "own".
      since we allow for partial matches, lunr will return a match with the following metadata:
      {
         metadata: {
           brown: {
             body: {
               position: [[11, 5]]
             }
           }
         }
       }
       so (2) we need to calculate the actual start position of the search term.
    */

      const metaData = match.matchData.metadata as MetaData;
      // step (1) described above, where we check all positions are continuous (with one space in between)
      const wordAndPositions = Object.entries(metaData)
        .map(([word, metadata]) => ({
          word: word,
          startPosition: metadata.body.position[0][0],
        }))
        .sort(({ startPosition }) => startPosition);
      const positionsAreContinuous = wordAndPositions.reduce(
        ([currentPositionsAreContinuous, nextExpectedPosition], { word, startPosition }) => [
          currentPositionsAreContinuous && nextExpectedPosition === startPosition,
          startPosition + word.length + 1,
        ],
        [true, wordAndPositions[0].startPosition],
      )[0];
      if (!positionsAreContinuous) return undefined;

      // step (2) described above, where we return the actual start position of the search term.
      const searchTextIndex =
        wordAndPositions[0].startPosition + wordAndPositions[0].word.indexOf(searchTerms[0]);
      return {
        startPosition: searchTextIndex,
        searchTerm: searchText,
        pageNumber: pageIndex,
        lineNumber: lineIndex,
      };
    })
    .filter((x) => x != null);
};
